import React, { useState } from "react";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const today = () => new Date().toLocaleDateString("en-CA");
const br = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
const future = (d) => !!d && d > today();

export const ScheduleChips = ({ b, showPayment = true }) => (
  <>
    {future(b.scheduled_start_date) && (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-sky-100 text-sky-800 border border-sky-200 px-2 py-0.5 rounded-full" data-testid={`badge-start-${b.id}`}>
        <CalendarClock className="w-3 h-3" /> Início programado {br(b.scheduled_start_date)}
      </span>
    )}
    {showPayment && future(b.expected_payment_date) && (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-teal-100 text-teal-800 border border-teal-200 px-2 py-0.5 rounded-full" data-testid={`badge-pay-${b.id}`}>
        <CalendarClock className="w-3 h-3" /> Recebe {br(b.expected_payment_date)}
      </span>
    )}
  </>
);

const DateRow = ({ label, hint, value, onChange, testid }) => (
  <div>
    <label className="block text-xs font-bold text-slate-700 mb-1">{label}</label>
    <div className="flex gap-2">
      <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-10" />
      {value && <Button type="button" variant="ghost" onClick={() => onChange("")} data-testid={`${testid}-clear`} className="h-10 text-rose-600">Tirar</Button>}
    </div>
    <p className="text-[11px] text-slate-500 mt-1">{hint}</p>
  </div>
);

export const BudgetScheduleButton = ({ budget, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState("");
  const [pay, setPay] = useState("");
  const [saving, setSaving] = useState(false);
  const show = () => { setStart(budget.scheduled_start_date || ""); setPay(budget.expected_payment_date || ""); setOpen(true); };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/budgets/${budget.id}/schedule`, { scheduled_start_date: start || null, expected_payment_date: pay || null });
      toast.success(start || pay ? "Programação salva" : "Programação removida");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };
  const active = future(budget.scheduled_start_date) || future(budget.expected_payment_date);
  return (
    <>
      <Button onClick={show} variant="outline" data-testid="btn-schedule-budget"
        className={active ? "border-sky-400 text-sky-800 bg-sky-50 hover:bg-sky-100" : "border-sky-300 text-sky-700 hover:bg-sky-50"}>
        <CalendarClock className="w-4 h-4 mr-2" /> {active ? "Programado" : "Programar"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1e3a8a]">Programar orçamento Nº {budget.number}</DialogTitle>
            <DialogDescription>Enquanto a data não chega, ele fica na aba "Programados". Você recebe um lembrete 7 dias antes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <DateRow label="Início previsto do serviço" value={start} onChange={setStart} testid="schedule-start"
              hint="Ex.: só será feito no recesso. A escola vê essa data no portal." />
            <DateRow label="Recebimento previsto" value={pay} onChange={setPay} testid="schedule-pay"
              hint="Ex.: finalizado, mas a escola só paga no ano que vem. Só você vê." />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="schedule-save" className="flex-1 bg-[#7c3aed] hover:bg-[#6d28d9] text-white">{saving ? "Salvando..." : "Salvar"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
